import {createSlice, createAsyncThunk, createAction} from '@reduxjs/toolkit';
import ProviderManager from 'app/provider/providerManager';
import {ErrorsEnum} from './Enums/ErrorsEnum';
import {showAppLoader, hideAppLoader} from './../../Shared/Loader/LoaderSlice';
import {getSubscriberInfo} from './Utils/SubscriberUtils';
import {dwhFuntoneSongDownloadFailed, dwhFuntoneChangePackageFailed, dwhFuntoneNewSubscriptionSuccess, dwhFuntoneSongDownloadSuccess} from './Reports/dwhReports';
import {getCommonReportParams} from './Reports/reportHelpers';

export const doSearch = createAsyncThunk('HeaderSearch/doSearch', async (data, thunkAPI) => {
  const {query, searchId} = typeof data === 'string' ? {query: data, searchId: null} : data;
  try {
    const result = await ProviderManager.getProviderData('fundialSearch', [query]);
    return {
      items: result.Body.itemsFolders,
      searchId,
      query
    };
  } catch (error) {
    console.error('❌ [API] doSearch thunk failed:', error);
    return thunkAPI.rejectWithValue(error);
  }
});

export const getCatalog = createAsyncThunk('funtone/getCategory', async (data, thunkAPI) => {
  try {
    const result = await ProviderManager.getProviderData('fundialGetCategory', [data]);
    return {results: {id: data, folders: result.Body.itemsFolders.folder, items: result.Body.itemsFolders.item}, errorCode: 0};
  } catch (error) {
    return thunkAPI.rejectWithValue({errorCode: error.errorCode});
  }
});

export const purchaseSong = createAsyncThunk('funtone/purchaseSong', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const state = thunkAPI.getState().FuntoneSlice;

    // Get aocDetails which contains the order strings
    const aocDetails = state.aocDetails;
    if (!aocDetails) {
      thunkAPI.dispatch(hideAppLoader());
      return thunkAPI.rejectWithValue({errorCode: ErrorsEnum.GENERAL_ERROR, message: 'Missing AOC details'});
    }

    // Determine if it's a premium package based on fundialPurchaseType
    const isPremium = aocDetails.fundialPurchaseType === 'PremiumItem';

    // Get order string based on purchase type
    let orderString = null;
    switch (aocDetails.fundialPurchaseType) {
      case 'PremiumItem':
      case 'NewCustomerPremiumItem':
      case 'NewCustomerRegularItem':
      case 'NewCustomerRegularItemAndPremiumItemOptions':
        orderString = aocDetails.premiumOrderString;
        break;
      case 'RegularItem':
      case 'RegularItemWithPremiumItemUpgradeOptions':
        orderString = aocDetails.orderString;
        break;
      default:
        orderString = aocDetails.orderString || aocDetails.premiumOrderString;
        break;
    }

    if (!orderString) {
      thunkAPI.dispatch(hideAppLoader());
      return thunkAPI.rejectWithValue({errorCode: ErrorsEnum.GENERAL_ERROR, message: 'Missing order string'});
    }

    // Check if this is the first song
    const isDefault = aocDetails.isFirstSong;

    const result = await ProviderManager.getProviderData('fundialPurchase', [data.subscriber, orderString, isDefault, isPremium]);
    

    // Check if the purchase was successful
    const isSuccess = result?.Body?.isSuccess !== false;
    
    if (!isSuccess) {
      thunkAPI.dispatch(hideAppLoader());
      return thunkAPI.rejectWithValue({
        errorCode: result?.Body?.errorCode || ErrorsEnum.SUBMIT_PACKAGE_FAILED,
        returnCodeMessage: result?.Body?.returnCodeMessage || 'Purchase failed'
      });
    }
    
    // Refresh gallery after purchase
    await thunkAPI.dispatch(getGallery());
    
    // If this is the first song, set it as default
    if ((isDefault && data.selectedSong) || (aocDetails.isFirstSong && data.selectedSong)) {
      // Get the product ID from selectedSong or aocDetails
      const productId = data.selectedSong.id || data.selectedSong.productId;
      if (productId) {
        await thunkAPI.dispatch(
          setDefaultSong({
            subscriber: data.subscriber,
            productId: productId,
          }),
        );
      }
    }
    
    thunkAPI.dispatch(hideAppLoader());

    return {results: {isSuccess: true}, errorCode: 0};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode, returnCodeMessage: error.returnCodeMessage});
  }
});

export const submitPackageSelect = createAsyncThunk('funtone/submitPackageSelect', async (data, thunkAPI) => {
  try {
    // Handle both old format (direct selectedPackage) and new format (object with selectedPackage and loginData)
    const selectedPackage = data?.selectedPackage || data;
    const loginData = data?.loginData;
    
    const state = thunkAPI.getState().FuntoneSlice;
    const subscriber = state.selectedSubscriber;
    const cli = typeof subscriber === 'string' ? subscriber : subscriber?.cli;
    const selectedSong = state.selectedSong;

    if (!cli) {
      return thunkAPI.rejectWithValue({errorCode: ErrorsEnum.NO_SUBSCRIBER});
    }

    if (!selectedSong || !selectedSong.id) {
      return thunkAPI.rejectWithValue({errorCode: ErrorsEnum.NO_SONG});
    }

    // First, get AOC details for the song and subscriber
    const aocResult = await thunkAPI.dispatch(
      getAoc({
        subscriber: cli,
        productId: selectedSong.id,
      }),
    );

    if (aocResult.type.endsWith('/rejected')) {
      return thunkAPI.rejectWithValue(aocResult.payload);
    }

    // Check for specific AOC error codes that indicate download failure
    const aocError = aocResult.payload?.results?.aocDetails?.aocError;
    if (aocError == "4" || aocError == "5" || aocError == "1000") {
      return thunkAPI.rejectWithValue({
        errorCode: ErrorsEnum.GENERAL_ERROR,
        returnCodeMessage: 'Failed to download song - AOC error',
        aocError: aocError,
        selectedSong: selectedSong,
        subscriber: cli
      });
    }

    // Check if user has no service (first-time purchase)
    const hasService = subscriber?.hasService;
    
    // If user has no service, call changePricePlan first to add the package
    if (!hasService && selectedPackage?.code?.value) {
      const changePlanResult = await thunkAPI.dispatch(
        changePricePlan({
          subscriber: cli,
          newSocCode: selectedPackage.code.value,
          skipGallery: true,
        }),
      );

      // Check if changePricePlan was rejected OR returned isSuccess: false
      if (changePlanResult.type.endsWith('/rejected')) {
        return thunkAPI.rejectWithValue(changePlanResult.payload);
      }
      
      // Also check if the result payload indicates failure
      if (changePlanResult.payload?.isSuccess === false) {
        return thunkAPI.rejectWithValue({
          errorCode: changePlanResult.payload?.errorCode || ErrorsEnum.SUBMIT_PACKAGE_FAILED,
          returnCodeMessage: changePlanResult.payload?.returnCodeMessage || 'Failed to change price plan'
        });
      }
    }

    // Now call purchaseSong with the package
    const result = await thunkAPI.dispatch(
      purchaseSong({
        subscriber: cli,
        selectedSong: selectedSong,
        selectedPackage: selectedPackage,
      }),
    );
    
    if (result.type.endsWith('/fulfilled')) {
      return {results: {isSuccess: true}, errorCode: 0};
    } else {
      return thunkAPI.rejectWithValue(result.payload);
    }
  } catch (error) {
    return thunkAPI.rejectWithValue({errorCode: error.errorCode || ErrorsEnum.SUBMIT_PACKAGE_FAILED});
  }
});

export const getSubscribersData = createAsyncThunk('funtone/getSubscribers', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialGetSubscribers', []);
    thunkAPI.dispatch(hideAppLoader());

    // Get selfcarePage state for enrichment
    const state = thunkAPI.getState();
    const selfcarePage = state.selfcarePage;

    // Enrich all subscribers with names from selfcarePage
    const rawSubscribers = result.Body.subscribersData || [];
    const enrichedSubscribers = rawSubscribers.map((subscriber) => {
      const info = getSubscriberInfo(subscriber.cli, selfcarePage);
      return {
        ...subscriber,
        firstName: info.firstName,
        lastName: info.lastName,
        fullName: info.firstName + ' ' + info.lastName,
      };
    });

    // Attempt to read the logged-in user's contact number from authentication state
    let selectedCli = null;
    try {
      selectedCli = state.authentication.loginData.userDetails.contactNumber;
    } catch (e) {
      selectedCli = null;
    }

    return {subscribers: enrichedSubscribers, selectedCli, primaryContact: result.Body.primaryContact};
  } catch (error) {
    return thunkAPI.rejectWithValue({errorCode: error.errorCode});
  } finally {
    thunkAPI.dispatch(hideAppLoader());
  }
});

export const getGallery = createAsyncThunk('funtone/getGallery', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    // Get selected subscriber's CLI from state
    const state = thunkAPI.getState();
    const selectedSubscriber = state.FuntoneSlice.selectedSubscriber;
    const cli = typeof selectedSubscriber === 'string' ? selectedSubscriber : selectedSubscriber?.cli;

    const result = await ProviderManager.getProviderData('fundialGetGallery', [cli]);
    thunkAPI.dispatch(hideAppLoader());
    return {results: {subscriberGallery: result.Body.subscriberGallery}, errorCode: 0};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode});
  }
});

export const getAoc = createAsyncThunk('funtone/getAoc', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialGetAoc', [data.subscriber, data.productId]);
    thunkAPI.dispatch(hideAppLoader());
    return {results: {aocDetails: result.Body}, errorCode: 0};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    // Return error info to be handled by extraReducers
    return thunkAPI.rejectWithValue({errorCode: error.errorCode, returnCodeMessage: error.returnCodeMessage});
  }
});

export const deleteSong = createAsyncThunk('funtone/deleteSong', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialDeleteSong', [data.subscriber, data.productId]);
    // Refresh gallery after deletion
    thunkAPI.dispatch(getGallery());
    thunkAPI.dispatch(hideAppLoader());
    return {success: true};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode});
  }
});

export const addContact = createAsyncThunk('funtone/addContact', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialAddContact', [data.subscriber, data.songId, data.phoneNumbers]);
    thunkAPI.dispatch(hideAppLoader());

    // Check if the result has a ReturnCode of 91 (failure)
    const returnCode = result?.header?.ReturnCode || result?.ReturnCode || 0;
    if (returnCode === 91) {
      return {success: false, errorCode: 91};
    }

    return {success: true, errorCode: 0};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode || 91, returnCodeMessage: error.returnCodeMessage});
  }
});

export const deleteContact = createAsyncThunk('funtone/deleteContact', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialDeleteContact', [data.subscriber, data.profileId]);
    thunkAPI.dispatch(hideAppLoader());

    // Check if the result has a ReturnCode of 91 (failure)
    const returnCode = result?.header?.ReturnCode || result?.ReturnCode || 0;
    if (returnCode === 91) {
      return thunkAPI.rejectWithValue({errorCode: 91});
    }

    return {success: true, errorCode: 0};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode || 91});
  }
});

export const setDefaultSong = createAsyncThunk('funtone/setDefaultSong', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialSetDefaultSong', [data.subscriber, data.productId]);
    // Refresh gallery after setting default song
    await thunkAPI.dispatch(getGallery());
    thunkAPI.dispatch(hideAppLoader());
    return {success: true, isSuccess: true, errorCode: 0, subscriber: data.subscriber, productId: data.productId};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode});
  }
});

export const setShuffle = createAsyncThunk('funtone/setShuffle', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialSetShuffle', [data.subscriber, data.profileId, data.isSetShuffle]);
    // Refresh gallery after setting shuffle
    await thunkAPI.dispatch(getGallery());
    thunkAPI.dispatch(hideAppLoader());
    return {success: true, isSuccess: true, isSetShuffle: data.isSetShuffle, errorCode: 0};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode});
  }
});

export const changePricePlan = createAsyncThunk('funtone/changePricePlan', async (data, thunkAPI) => {
  try {
    thunkAPI.dispatch(showAppLoader());
    const result = await ProviderManager.getProviderData('fundialPurchasePricePlan', [data.subscriber, data.newSocCode]);
    
    // Check if the operation was successful
    const isSuccess = result?.Body?.isSuccess !== false;
    
    if (!isSuccess) {
      thunkAPI.dispatch(hideAppLoader());
      return thunkAPI.rejectWithValue({
        errorCode: result?.Body?.errorCode || ErrorsEnum.SUBMIT_PACKAGE_FAILED,
        returnCodeMessage: result?.Body?.returnCodeMessage || 'Failed to change price plan'
      });
    }
    
    // Refresh gallery after changing price plan
    if (!data.skipGallery) {
      await thunkAPI.dispatch(getGallery());
    }
    thunkAPI.dispatch(hideAppLoader());
    return {success: true, isSuccess: true, errorCode: 0, subscriber: data.subscriber, newSocCode: data.newSocCode};
  } catch (error) {
    thunkAPI.dispatch(hideAppLoader());
    return thunkAPI.rejectWithValue({errorCode: error.errorCode, returnCodeMessage: error.returnCodeMessage});
  }
});

export const ScreensEnum = {
  CATALOG: 'CATALOG',
  SEARCH: 'SEARCH',
  SONGS_PAGE: 'SONGS_PAGE',
  ERROR: 'ERROR',
  PACKAGE_SELECT: 'PACKAGE_SELECT',
  SUCCESS: 'SUCCESS',
  ADMIN_PANEL: 'ADMIN_PANEL',
  SONG_SETTINGS: 'SONG_SETTINGS',
  CONFIGURE_SONGS: 'CONFIGURE_SONGS',
  TERMS_AND_CONDITIONS: 'TERMS_AND_CONDITIONS',
  SUBSCRIBER_SELECT: 'SUBSCRIBER_SELECT',
};

export const PopUpEnum = {
  LOGIN: 'LOGIN',
  DELETE_SONG: 'DELETE_SONG',
  REMOVE_CONTACT: 'REMOVE_CONTACT',
};
export const LoginPopUpStages = {
  PHONE: 'PHONE',
  OTP: 'OTP',
  ERROR: 'ERROR',
};

export const FuntoneSubscriptionStatus = {
  UNREGISTERED: 'UNREGISTERED',
  NONE: 'NONE',
  ACTIVE: 'ACTIVE',
  SUSPENDED: 'SUSPENDED',
  REGULAR: 'REGULAR',
  PREMIUM: 'PREMIUM',
};

// Helper function to validate subscriber and return appropriate error
const validateSubscriber = (subscriber) => {
  const validationResults = subscriber?.validationResults;
  if (!validationResults) return null;

  // Check for business account
  if (validationResults.IsBusinessAccount) {
    return ErrorsEnum.IS_BUSINESS;
  }

  // Check for lost or stolen suspension
  if (validationResults.SubStatusLostOrStolen) {
    return ErrorsEnum.SUSPENDED_SUBSCRIBER_LOST_OR_STOLEN;
  }

  // Check for suspended dstatus
  if (validationResults.SubStatus) {
    return ErrorsEnum.SUSPENDED;
  }

  // Check for data only
  if (validationResults.SwitchCode) {
    return ErrorsEnum.DATA_ONLY;
  }

  // Check for ניוד (in transfer)
  if (validationResults.NPStatus) {
    return ErrorsEnum.IN_TRANSFER;
  }

  // Check for both debt and payment method issues
  if (validationResults.Debt && validationResults.PaymentMethod) {
    return ErrorsEnum.FINANCIAL_PAYMENT_UPDATE_ERROR;
  }

  // Check for debt only
  if (validationResults.Debt) {
    return ErrorsEnum.FINANCIAL_DEBT_ERROR;
  }

  // Check for payment method only
  if (validationResults.PaymentMethod) {
    return ErrorsEnum.FINANCIAL_PAYMENT_METHOD_UPDATE_ERROR;
  }

  return null;
};

export const saveToHistory = createAction('funtoneSlice/saveToHistory');

const FuntoneSlice = createSlice({
  name: 'funtoneSlice',
  initialState: {
    currentScreen: ScreensEnum.CATALOG,
    navigationStack: [], // Array to track navigation history for back button functionality
    errorReason: null,
    popUp: {
      type: null,
      stage: null,
      error: undefined,
      data: null, // Store data for the popup (e.g., contact to remove, song to remove from playlist)
    },
    categories: {},
    selectedGenre: null,
    selectedGenreSongs: [],
    search: {
      searchResults: [],
      isLoading: false,
      searchQuery: '',
      currentSearchId: 0, // Track the latest search ID
    },
    selectedSubscriber: null, // CLI string only
    selectedSong: null,
    selectedPackage: null,
    subscribersData: null, // Array of enriched subscriber objects
    isSubscribersFetching: false, // True while getSubscribersData is in-flight
    isSubscribersFetchFailed: false, // True after getSubscribersData rejects; prevents auto-retry
    aocDetails: null, // AOC details for purchase flow
    isChangePackageFlow: false, // Flag to distinguish package change vs song purchase flow
    isGalleryMode: false, // Flag to show subscriber's downloaded songs instead of genre songs
    subscriberGallery: [], // Downloaded songs for current subscriber
    isEditPlaylistFlow: false, // Flag to indicate editing playlist mode
    isAssignToContactFlow: false, // Flag to indicate navigation from "assign to contact" menu action
    isChangeDefaultSongFlow: false, // Flag to indicate change default song flow
    isAddServiceFlow: false, // Flag to indicate adding service without a song
    previousScreen: null, // Track intended back destination for context-aware navigation
    primaryContact: null,
    toast: {
      isVisible: false,
      message: '',
    },
  },
  reducers: {
    setCurrentScreen: (state, action) => {
      // Accept either a plain screen string or { screen, params }
      const payload = action.payload;
      const newScreen = typeof payload === 'object' && payload !== null ? payload.screen : payload;
      const params = typeof payload === 'object' && payload !== null ? (payload.params || null) : null;

      // Don't add to stack if it's the same as current screen
      if (state.currentScreen === newScreen) {
        return;
      }
      
      // Push current screen to navigation stack before changing (only for forward navigation)
      if (state.currentScreen) {
        // Initialize navigationStack if it doesn't exist
        if (!state.navigationStack) {
          state.navigationStack = [];
        }
        
        // Always add current screen to stack for forward navigation
        // Don't add duplicates if the last item in stack is the same as current screen.
        // SUBSCRIBER_SELECT is transient (auto-forwards immediately), so never put it in
        // the back stack — mirrors the replaceState treatment in browser history below.
        const isTransient = state.currentScreen === ScreensEnum.SUBSCRIBER_SELECT;
        if (!isTransient &&
            (state.navigationStack.length === 0 || 
            state.navigationStack[state.navigationStack.length - 1] !== state.currentScreen)) {
          state.navigationStack.push(state.currentScreen);
        }
        
        // Limit stack size to 20 items to prevent memory issues
        if (state.navigationStack.length > 20) {
          state.navigationStack.shift(); // Remove oldest entry
        }
      }
      
      // Update previous and current screen
      state.previousScreen = state.currentScreen;
      state.currentScreen = newScreen;
      
      // Add to browser history if we're in a browser environment
      if (typeof window !== 'undefined' && window.history && window.history.pushState) {
        try {
          let hash = `#${newScreen.toLowerCase()}`;
          if (params && Object.keys(params).length > 0) {
            const qs = Object.entries(params).map(([k, v]) => `${k}=${v}`).join('&');
            hash += `&${qs}`;
          }
          const stateData = {
            screen: newScreen,
            params,
            timestamp: Date.now(),
            isChangePackageFlow: state.isChangePackageFlow,
            isAddServiceFlow: state.isAddServiceFlow,
          };
          // SUBSCRIBER_SELECT is a transient screen that auto-forwards immediately.
          // When SUBSCRIBER_SELECT is the *source* (wasTransient), use replaceState so
          // we don't leave a dangling transient entry between the origin and PACKAGE_SELECT.
          // When SUBSCRIBER_SELECT is the *destination*, use pushState so the previous
          // screen (e.g. SONGS_PAGE) remains a distinct browser history entry the user
          // can reach with the browser back button.
          const wasTransient = state.previousScreen === ScreensEnum.SUBSCRIBER_SELECT;
          if (wasTransient) {
            window.history.replaceState(stateData, '', hash);
          } else {
            window.history.pushState(stateData, '', hash);
          }
        } catch (e) {
          console.warn('Failed to update browser history:', e);
        }
      }
    },
    setIsChangePackageFlow: (state, action) => {
      state.isChangePackageFlow = action.payload;
    },
    setIsGalleryMode: (state, action) => {
      state.isGalleryMode = action.payload;
    },
    setIsEditPlaylistFlow: (state, action) => {
      state.isEditPlaylistFlow = action.payload;
    },
    setIsAssignToContactFlow: (state, action) => {
      state.isAssignToContactFlow = action.payload;
    },
    setIsChangeDefaultSongFlow: (state, action) => {
      state.isChangeDefaultSongFlow = action.payload;
    },
    setIsAddServiceFlow: (state, action) => {
      state.isAddServiceFlow = action.payload;
    },
    showToast: (state, action) => {
      if (!state.toast) {
        state.toast = {isVisible: false, message: ''};
      }
      state.toast.isVisible = true;
      state.toast.message = action.payload;
    },
    hideToast: (state) => {
      if (!state.toast) {
        state.toast = {isVisible: false, message: ''};
      }
      state.toast.isVisible = false;
      state.toast.message = '';
    },
    setPopUp: (state, action) => {
      state.popUp = action.payload;
    },
    // Set a global error reason to display on the error screen
    setErrorReason: (state, action) => {
      state.errorReason = action.payload;
    },
    // Close any popup
    closePopUp: (state) => {
      state.popUp = null;
    },
    // Set the stage of the login popup
    setLoginPopUpStage: (state, action) => {
      if (state.popUp && state.popUp.type === PopUpEnum.LOGIN) {
        state.popUp.stage = action.payload;
      }
    },
    // Set error for login popup
    setLoginPopUpError: (state, action) => {
      if (state.popUp && state.popUp.type === PopUpEnum.LOGIN) {
        state.popUp.error = action.payload;
      }
    },
    // Clear error for login popup
    clearLoginPopUpError: (state) => {
      if (state.popUp && state.popUp.type === PopUpEnum.LOGIN) {
        state.popUp.error = undefined;
      }
    },
    setSelectedGenre: (state, action) => {
      state.selectedGenre = action.payload.genre;
      state.selectedGenreSongs = action.payload.songs || [];
    },
    clearSearchResults: (state) => {
      state.search.searchResults = [];
      state.search.searchQuery = '';
    },
    setSearchQuery: (state, action) => {
      state.search.searchQuery = action.payload;
    },

    goBack: (state) => {
      console.log('Navigation stack:', state.navigationStack);
      
      // When going back from PACKAGE_SELECT with a package explicitly selected:
      // first click deselects the package (stays on page), second click navigates back.
      // Check for an actual package with a code value — guards against empty Proxy objects
      // that can appear after state restoration from session storage (QA2 behaviour).
      if (state.currentScreen === ScreensEnum.PACKAGE_SELECT && state.selectedPackage?.code?.value) {
        state.selectedPackage = null;
        return;
      }

      // Special handling for flows when going back from SONGS_PAGE - clear all flow flags and go to ADMIN_PANEL
      if (state.currentScreen === ScreensEnum.SONGS_PAGE && 
          (state.isAssignToContactFlow || state.isEditPlaylistFlow || state.isChangeDefaultSongFlow)) {
        state.previousScreen = state.currentScreen;
        state.currentScreen = ScreensEnum.ADMIN_PANEL;
        // Clear all flow flags
        state.isAssignToContactFlow = false;
        state.isEditPlaylistFlow = false;
        state.isChangeDefaultSongFlow = false;
        // Clear navigation stack as we're going back to admin panel
        state.navigationStack = state.navigationStack ? state.navigationStack.filter(s => s !== ScreensEnum.SONGS_PAGE) : [];
        return;
      }
      
      // When going back from SONG_SETTINGS to SONGS_PAGE:
      // - If ONLY isAssignToContactFlow is active (not combined with other flows), preserve it
      // - If isAssignToContactFlow is combined with isEditPlaylistFlow, clear only isAssignToContactFlow
      if (state.currentScreen === ScreensEnum.SONG_SETTINGS) {
        // Clear isAssignToContactFlow only if it's part of another flow (like edit playlist)
        if (state.isAssignToContactFlow && (state.isEditPlaylistFlow || state.isChangeDefaultSongFlow)) {
          state.isAssignToContactFlow = false;
        }
        // Continue with normal back navigation below
      }
      
      // Special handling for success and error pages - navigate to last CATALOG or ADMIN_PANEL
      if (state.currentScreen === ScreensEnum.SUCCESS || state.currentScreen === ScreensEnum.ERROR) {
        // Initialize navigationStack if it doesn't exist
        if (!state.navigationStack) {
          state.navigationStack = [];
        }
        
        // Find the most recent catalog or admin panel in navigation stack
        for (let i = state.navigationStack.length - 1; i >= 0; i--) {
          if (state.navigationStack[i] === ScreensEnum.CATALOG || state.navigationStack[i] === ScreensEnum.ADMIN_PANEL) {
            state.currentScreen = state.navigationStack[i];
            // Clear the navigation stack up to this point to prevent revisiting intermediate screens
            state.navigationStack = state.navigationStack.slice(0, i);
            return;
          }
        }
        
        // Default to catalog if neither found in stack
        state.currentScreen = ScreensEnum.CATALOG;
        state.navigationStack = []; // Clear stack as we're going to root
        return;
      }
      
      // For normal navigation, pop from the navigation stack
      if (!state.navigationStack) {
        state.navigationStack = [];
      }
      
      if (state.navigationStack.length > 0) {
        // Get the previous screen from the stack
        const previousScreen = state.navigationStack.pop();
        state.previousScreen = state.currentScreen;
        state.currentScreen = previousScreen;
      } else {
        // If no history, default to catalog
        state.previousScreen = state.currentScreen;
        state.currentScreen = ScreensEnum.CATALOG;
      }
    },
    handleBrowserBack: (state, action) => {
      // This reducer handles the browser back button (popstate event)
      // It synchronizes the Redux state with the browser history
      const historyState = action.payload; // Browser history state from popstate event
      
      if (historyState && historyState.screen) {
        // SUBSCRIBER_SELECT is transient — it auto-forwards to PACKAGE_SELECT as soon as
        // it renders. If the browser lands on this entry (e.g. the user pressed back
        // through a pushState entry for it), skip it and use the navigation stack instead
        // so the user visibly lands on the real previous screen.
        if (historyState.screen === ScreensEnum.SUBSCRIBER_SELECT) {
          state.popUp = null;
          if (state.navigationStack && state.navigationStack.length > 0) {
            state.previousScreen = state.currentScreen;
            state.currentScreen = state.navigationStack.pop();
          } else {
            state.previousScreen = state.currentScreen;
            state.currentScreen = ScreensEnum.CATALOG;
          }
          return;
        }

        // If navigating to SONGS_PAGE with a genre ID, restore selectedGenre with full data
        if (historyState.screen === ScreensEnum.SONGS_PAGE && historyState.params?.id) {
          const genreId = historyState.params.id;
          const folder = state.categories?.[0]?.folders?.find((f) => f.id === genreId);
          state.selectedGenre = {
            id: genreId,
            image: folder?.image || null,
            title: folder?.description || null,
            description: folder?.description || null,
          };
          state.selectedGenreSongs = [];
        }

        // If we have a screen in the browser history state, navigate to it.
        // Always dismiss any open popup on browser back — modals should not
        // survive a back-navigation.
        state.popUp = null;
        state.previousScreen = state.currentScreen;
        state.currentScreen = historyState.screen;

        // Restore flow flags that were saved when this history entry was pushed
        if (historyState.isChangePackageFlow !== undefined) {
          state.isChangePackageFlow = historyState.isChangePackageFlow;
        }
        if (historyState.isAddServiceFlow !== undefined) {
          state.isAddServiceFlow = historyState.isAddServiceFlow;
        }
        
        // Don't manipulate the navigation stack here as this is triggered by browser back
        // The navigation stack should remain as is to maintain consistency
      } else {
        // No Redux state attached to this history entry (e.g. created by a pushPath call
        // from another system). Try to derive the screen from the URL hash so we don't
        // always fall back to CATALOG.
        state.popUp = null;
        state.previousScreen = state.currentScreen;
        const hashKey =
          typeof window !== 'undefined'
            ? window.location.hash.substring(1).toUpperCase().split('&')[0]
            : '';
        state.currentScreen =
          hashKey && ScreensEnum[hashKey] ? ScreensEnum[hashKey] : ScreensEnum.CATALOG;
      }
    },
    setSelectedSubscriber: (state, action) => {
      const subscriber = action.payload;
      state.selectedSubscriber = subscriber;
    },
    setSelectedSong: (state, action) => {
      state.selectedSong = action.payload;
    },
    setSelectedPackage: (state, action) => {
      state.selectedPackage = action.payload;
    },
    resetState: (state) => initialState
  },
  extraReducers: {
    [doSearch.pending]: (state, action) => {
      const searchId = action.meta?.arg?.searchId;
      if (searchId != null) {
        state.search.currentSearchId = searchId;
      }
      state.search.isLoading = true;
      // Keep previous results visible until new ones arrive to avoid flash of empty
    },
    [doSearch.fulfilled]: (state, action) => {
      const {items, searchId} = action.payload;

      // Reject stale results — only accept if this matches the latest dispatched search
      if (searchId != null && searchId !== state.search.currentSearchId) {
        state.search.isLoading = false;
        return;
      }

      state.search.isLoading = false;
      if (searchId != null) {
        state.search.currentSearchId = searchId;
      }
      const resultItems = items?.item || [];
      state.search.searchResults = resultItems.map((item) => ({
        ...item,
        isDownloadable: true,
        isPlayable: true,
      }));
    },
    [doSearch.rejected]: (state) => {
      console.warn('❌ [REDUX] doSearch.rejected - clearing results');
      state.search.isLoading = false;
      state.search.searchResults = [];
    },
    [getCatalog.fulfilled]: (state, action) => {
      if (action.payload.errorCode == 0) {
        state.categories[action.payload.results.id] = {
          isFolders: action.payload.results.folders.length != 0,
          folders: action.payload.results.folders,
          items: action.payload.results.items,
        };
      }
    },
    [getCatalog.rejected]: (state, action) => {
      // Handle getCatalog rejection if needed
      console.error('getCatalog failed:', action.payload);
    },
    [submitPackageSelect.fulfilled]: (state, action) => {
      console.log('Package submitted successfully:', action.payload);
      
      // Get current state values for reporting
      const selectedSubscriber = state.selectedSubscriber;
      const selectedSong = state.selectedSong;
      const isChangePackageFlow = state.isChangePackageFlow;
      const isAddServiceFlow = state.isAddServiceFlow;
      
      // Determine if this is a new subscription
      const isNewSubscription = !isChangePackageFlow && !isAddServiceFlow && selectedSong && selectedSubscriber && !selectedSubscriber.hasService;
      
      // Get loginData from the thunk argument
      const loginData = action.meta?.arg?.loginData;
      
      if (isNewSubscription && loginData && selectedSubscriber) {
        const reportParams = getCommonReportParams(selectedSubscriber, loginData);
        const isExistingSubscriber = 'לא מנוי המתנה נעימה';
        dwhFuntoneNewSubscriptionSuccess({
          ...reportParams,
          ban: loginData?.userDetails?.ban || '',
          crmId: loginData?.userDetails?.crmId || '',
          isExistingSubscriber,
        });
      } else if (selectedSong && selectedSubscriber?.hasService && loginData) {
        const reportParams = getCommonReportParams(selectedSubscriber, loginData);
        dwhFuntoneSongDownloadSuccess({
          ...reportParams,
          ban: loginData?.userDetails?.ban || '',
          crmId: loginData?.userDetails?.crmId || '',
          songName: selectedSong?.description || selectedSong?.title || '',
        });
      }
      
      state.currentScreen = ScreensEnum.SUCCESS;
    },
    [submitPackageSelect.rejected]: (state, action) => {
      // Check if this is an AOC error and report song download failure
      if (action.payload?.aocError && action.payload?.selectedSong && action.payload?.subscriber) {
        dwhFuntoneSongDownloadFailed({
          subscriber: action.payload.subscriber,
          customerName: '',
          id: action.payload.selectedSong.id,
          ban: '',
          crmId: '',
          songName: action.payload.selectedSong.description || action.payload.selectedSong.title || ''
        });
      }
      
      state.errorReason = ErrorsEnum.SUBMIT_PACKAGE_FAILED;
      state.currentScreen = ScreensEnum.ERROR;
    },
    [getSubscribersData.pending]: (state) => {
      state.isSubscribersFetching = true;
      state.isSubscribersFetchFailed = false;
    },
    [getSubscribersData.fulfilled]: (state, action) => {
      state.isSubscribersFetching = false;
      const subscribers = action.payload.subscribers || [];
      const primaryContact = action.payload.primaryContact;
      
      // Sort subscribers: primary contact first, then those with service, then those without
      const sortedSubscribers = subscribers.sort((a, b) => {
        // Primary contact always comes first
        const aIsPrimary = primaryContact && a.cli === primaryContact;
        const bIsPrimary = primaryContact && b.cli === primaryContact;
        
        if (aIsPrimary && !bIsPrimary) return -1;
        if (!aIsPrimary && bIsPrimary) return 1;
        
        // If both are primary or both are not primary, sort by service status
        if (a.hasService === b.hasService) {
          return 0; // Keep original order for same service status
        }
        return a.hasService ? -1 : 1; // hasService first (true comes before false)
      });
      
      state.subscribersData = sortedSubscribers;
      state.primaryContact = primaryContact;
      // Auto-select the logged-in user's CLI if provided
      const selectedCli = action.payload.selectedCli;
      if (selectedCli && !state.selectedSubscriber) {
        state.selectedSubscriber = sortedSubscribers.find((sub) => sub.cli === selectedCli);
      } else if (state.selectedSubscriber) {
        // If we already have a selected subscriber, refresh it from the new list
        const currentCli = typeof state.selectedSubscriber === 'string' ? state.selectedSubscriber : state.selectedSubscriber?.cli;
        if (currentCli) {
          const fresh = sortedSubscribers.find((sub) => sub.cli === currentCli);
          if (fresh) {
            state.selectedSubscriber = fresh;
          }
        }
      }
      
      // Check if selectedSubscriber has validation errors
      if (state.selectedSubscriber && typeof state.selectedSubscriber === 'object') {
        const validationError = validateSubscriber(state.selectedSubscriber);
        if (validationError) {
          state.errorReason = validationError;
          state.currentScreen = ScreensEnum.ERROR;
        }
      }
    },
    [getSubscribersData.rejected]: (state, action) => {
      state.isSubscribersFetching = false;
      state.isSubscribersFetchFailed = true;
      console.error('getSubscribersData failed:', action.payload);

      // Handle navigation to error screen based on error code
      const errorCode = action.payload?.errorCode;
      switch (errorCode) {
        case 89:
          state.errorReason = ErrorsEnum.NO_SUBSCRIBERS;
          break;
        default:
          state.errorReason = ErrorsEnum.GENERAL_ERROR;
      }
      state.currentScreen = ScreensEnum.ERROR;
    },
    [getGallery.fulfilled]: (state, action) => {
      if (action.payload.errorCode === 0) {
        const subscriberGallery = action.payload.results.subscriberGallery;
        const galleryItems = subscriberGallery?.items?.item || [];
        state.subscriberGallery = galleryItems.map((item) => ({
          id: item.productId || item.funId,
          productId: item.productId,
          description: item.title,
          author: item.artists?.artistName || '',
          image: item.image,
          duration: item.duration,
          funtonPrevUrl: item.funtonPrevUrl,
          audioUrl: item.audioUrl,
          isInUse: item.isInUse === 'true',
          isDefault: item.isDefault === 'true',
          isDownloadable: false,
          isPlayable: false,
          profiles: item.profiles, // Include profiles for contact management
          menuActions: [
            {key: 'set-as-default', label: 'שיר ברירת מחדל'},
            {key: 'assign-to-contact', label: 'התאמה לאיש קשר'},
            {key: 'delete-song', label: 'מחיקת שיר'},
          ],
          funtonPrevUrl: item?.funtonPrevUrl,
        }));
        console.log(subscriberGallery);
      }
    },
    [getGallery.rejected]: (state, action) => {
      console.error('getGallery failed:', action.payload);
      state.subscriberGallery = [];
    },
    [getAoc.fulfilled]: (state, action) => {
      if (action.payload.errorCode === 0) {
        state.aocDetails = action.payload.results.aocDetails;
      }
    },
    [getAoc.rejected]: (state, action) => {
      console.error('getAoc failed:', action.payload);
      state.aocDetails = null;

      // Handle navigation to error screen based on error code
      const errorCode = action.payload?.errorCode;
      switch (errorCode) {
        case 21:
        case 22:
        case 23:
        case 24:
        case 89:
          state.errorReason = ErrorsEnum.SUBSCRIBER_ERROR;
          state.currentScreen = ScreensEnum.ERROR;
          break;
        case 20:
          state.errorReason = ErrorsEnum.SONG_EXIST;
          state.currentScreen = ScreensEnum.ERROR;
          break;
        default:
          state.errorReason = ErrorsEnum.GENERAL_ERROR;
          state.currentScreen = ScreensEnum.ERROR;
      }
    },
    [deleteSong.fulfilled]: (state, action) => {
      console.log('Song deleted successfully');
      state.toast = {
        isVisible: true,
        message: 'השיר נמחק בהצלחה'
      };
    },
    [deleteSong.rejected]: (state, action) => {
      console.error('deleteSong failed:', action.payload);
      state.toast = {
        isVisible: true,
        message: 'שגיאה במחיקת השיר'
      };
    },
    [addContact.fulfilled]: (state, action) => {
      console.log('Contact added successfully');
      // Check if there's an error code in the response
      if (action.payload?.errorCode === 91) {
        state.toast = {
          isVisible: true,
          message: 'שגיאה בהוספת איש הקשר'
        };
      } else {
        state.toast = {
          isVisible: true,
          message: 'איש הקשר נוסף בהצלחה'
        };
      }
    },
    [addContact.rejected]: (state, action) => {
      console.error('addContact failed:', action.payload);
      state.toast = {
        isVisible: true,
        message: 'שגיאה בהוספת איש הקשר'
      };
    },
    [deleteContact.fulfilled]: (state, action) => {
      console.log('Contact deleted successfully');
      state.toast = {
        isVisible: true,
        message: 'איש הקשר הוסר בהצלחה'
      };
    },
    [deleteContact.rejected]: (state, action) => {
      console.error('deleteContact failed:', action.payload);
      state.toast = {
        isVisible: true,
        message: 'שגיאה בהסרת איש הקשר'
      };
    },
    [setDefaultSong.fulfilled]: (state, action) => {
      console.log('Default song set successfully');
      
      const {subscriber, productId} = action.payload;
      
      // Find the song details from the gallery
      const defaultSong = state.subscriberGallery.find(song => 
        song.productId === productId || song.id === productId
      );
      
      // Update selectedSubscriber
      if (state.selectedSubscriber) {
        const selectedCli = typeof state.selectedSubscriber === 'string' ? state.selectedSubscriber : state.selectedSubscriber?.cli;
        if (selectedCli === subscriber) {
          if (typeof state.selectedSubscriber === 'object') {
            state.selectedSubscriber.isShuffleMode = false;
            if (defaultSong) {
              state.selectedSubscriber.defaultSongId = defaultSong.productId || defaultSong.id;
              state.selectedSubscriber.defaultSongTitle = defaultSong.description;
              state.selectedSubscriber.defaultSongImage = defaultSong.image;
              state.selectedSubscriber.defaultSongArtistName = defaultSong.author;
            }
          }
        }
      }
      
      // Update subscribersData array
      if (state.subscribersData) {
        const subscriberIndex = state.subscribersData.findIndex(sub => sub.cli === subscriber);
        if (subscriberIndex !== -1) {
          state.subscribersData[subscriberIndex].isShuffleMode = false;
          if (defaultSong) {
            state.subscribersData[subscriberIndex].defaultSongId = defaultSong.productId || defaultSong.id;
            state.subscribersData[subscriberIndex].defaultSongTitle = defaultSong.description;
            state.subscribersData[subscriberIndex].defaultSongImage = defaultSong.image;
            state.subscribersData[subscriberIndex].defaultSongArtistName = defaultSong.author;
          }
        }
      }
      
      state.toast = {
        isVisible: true,
        message: 'השיר הוגדר בהצלחה'
      };
    },
    [setDefaultSong.rejected]: (state, action) => {
      console.error('setDefaultSong failed:', action.payload);
      state.toast = {
        isVisible: true,
        message: 'שגיאה בהגדרת השיר הקבוע'
      };
    },
    [setShuffle.fulfilled]: (state, action) => {
      console.log('Shuffle set successfully');
      state.selectedSubscriber.isShuffleMode = true;
      const subscriberIndex = state.subscribersData.findIndex(sub => sub.cli === state.selectedSubscriber.cli);
      if (subscriberIndex !== -1) {
        state.subscribersData[subscriberIndex].isShuffleMode = true;
      }
      state.toast = {
        isVisible: true,
        message: 'מצב אקראי הופעל בהצלחה'
      };
    },
    [setShuffle.rejected]: (state, action) => {
      console.error('setShuffle failed:', action.payload);
      state.toast = {
        isVisible: true,
        message: 'שגיאה בהפעלת מצב אקראי'
      };
    },
    [changePricePlan.fulfilled]: (state, action) => {
      console.log('Price plan changed successfully');
      
      // Update subscriber state with new package code and hasService flag
      const {subscriber, newSocCode} = action.payload;
      
      if (subscriber && newSocCode) {
        // Update selectedSubscriber if it matches
        if (state.selectedSubscriber) {
          const selectedCli = typeof state.selectedSubscriber === 'string' ? state.selectedSubscriber : state.selectedSubscriber?.cli;
          if (selectedCli === subscriber) {
            // If selectedSubscriber is an object, update it
            if (typeof state.selectedSubscriber === 'object') {
              state.selectedSubscriber.packageCode = newSocCode;
              state.selectedSubscriber.hasService = true;
            }
          }
        }
        
        // Update subscribersData array
        if (state.subscribersData) {
          const subscriberIndex = state.subscribersData.findIndex(sub => sub.cli === subscriber);
          if (subscriberIndex !== -1) {
            state.subscribersData[subscriberIndex].packageCode = newSocCode;
            state.subscribersData[subscriberIndex].hasService = true;
          }
        }
      }
    },
    [changePricePlan.rejected]: (state, action) => {
      console.error('changePricePlan failed:', action.payload);
      
      // Report change package failure
      const selectedSubscriber = state.selectedSubscriber;
      const cli = typeof selectedSubscriber === 'string' ? selectedSubscriber : selectedSubscriber?.cli;
      dwhFuntoneChangePackageFailed({
        subscriber: cli || '',
        customerName: '',
        id: '',
        ban: '',
        crmId: '',
        packageType: state.selectedPackage?.code?.value || ''
      });
      
      state.errorReason = ErrorsEnum.SUBMIT_PACKAGE_FAILED;
      state.currentScreen = ScreensEnum.ERROR;
    },
    [purchaseSong.fulfilled]: (state, action) => {
      console.log('Song purchased successfully');
    },
    [purchaseSong.rejected]: (state, action) => {
      console.error('purchaseSong failed:', action.payload);

      // Handle navigation to error screen based on error code
      const errorCode = action.payload?.errorCode;
      switch (errorCode) {
        case 12:
          state.errorReason = ErrorsEnum.PLAYLIST_EXCEED_ERROR;
          state.currentScreen = ScreensEnum.ERROR;
          break;
        default:
          state.errorReason = ErrorsEnum.GENERAL_ERROR;
          state.currentScreen = ScreensEnum.ERROR;
      }
    },
  },
});

export const {
  setErrorReason,
  setCurrentScreen,
  setPopUp,
  closePopUp,
  setLoginPopUpStage,
  setLoginPopUpError,
  clearLoginPopUpError,
  clearSearchResults,
  setSearchQuery,
  setSelectedGenre,
  goBack,
  handleBrowserBack,
  setSelectedSubscriber,
  setSelectedSong,
  setSelectedPackage,
  setIsChangePackageFlow,
  setIsGalleryMode,
  setIsEditPlaylistFlow,
  setIsAssignToContactFlow,
  setIsChangeDefaultSongFlow,
  setIsAddServiceFlow,
  showToast,
  hideToast,
  resetState,
} = FuntoneSlice.actions;

export default FuntoneSlice.reducer;
